import { ref, computed } from '@vue/composition-api'
import { apiToastError, apiToastSuccess } from '@/@core/utils/toast'
import { kpEndpoint, kpRequest } from '@/axios'
import analytics from '@/@core/utils/analytics'
import constants from '@/constants'
import store from '@/store'

const { SHARE_MULTIPLE_SELECTIONS } = constants.TRACKS.ACTIONS

function buildFilteredSelections(messagesRef) {
  const successfullySharedSelections = computed(
    () => messagesRef.value.filter(message => !message.isError) || [],
  )

  const failedSharedSelections = computed(
    () => messagesRef.value.filter(message => message.isError) || [],
  )

  const isAllSuccess = computed(
    () => successfullySharedSelections.value?.length === messagesRef.value?.length,
  )

  const isAllFailed = computed(
    () => failedSharedSelections.value?.length === messagesRef.value?.length,
  )

  return {
 successfullySharedSelections, failedSharedSelections, isAllSuccess, isAllFailed,
}
}

function buildAllSuccessSubHeader(count, retailerName) {
  if (count === 1) {
    return `You successfully shared the selection with ${retailerName}:`
  }
  return `You successfully shared ${count} selections with ${retailerName}:`
}

function buildAllFailedSubHeader(failedCount, retailerName) {
  if (failedCount === 1) {
    return `You already shared the chosen selection with ${retailerName}. Do you want to follow up on this selection?`
  }
  return `You already shared chosen selections with ${retailerName}. Do you want to follow-up on these selections?`
}

function buildSubHeader({
 isAllSuccess, isAllFailed, successfullySharedSelections, failedSharedSelections, seletedRetailer,
}) {
  return computed(() => {
    const retailerName = seletedRetailer.value?.entityName || 'retailer'

    if (isAllSuccess.value) {
      return buildAllSuccessSubHeader(successfullySharedSelections.value.length, retailerName)
    }

    if (isAllFailed.value) {
      return buildAllFailedSubHeader(failedSharedSelections.value.length, retailerName)
    }

    return `You already shared some of the chosen selections with ${retailerName}. Do you want to follow-up on already shared selections?`
  })
}

function sendFollowUpRequest(payload) {
  return kpRequest({
    ...kpEndpoint.wishlist.followUp,
    payload,
  })
}

function handleFollowUpSuccess(res, closeModalFn, trackObject) {
  closeModalFn()
  apiToastSuccess(res?.data?.message || 'Email follow-up sent. You could see open and click data in the Sales Insights section.')
  analytics.track(SHARE_MULTIPLE_SELECTIONS.FOLLOW_UP_SUCCESSED, trackObject)
}

function handleFollowUpError(err, trackObject) {
  apiToastError(err)
  console.error(err)
  analytics.track(SHARE_MULTIPLE_SELECTIONS.FOLLOW_UP_FAILED, trackObject)
}

export function useShareSelectionsError(messagesRef) {
  const isProcessingFollowUp = ref(false)

  const {
 successfullySharedSelections, failedSharedSelections, isAllSuccess, isAllFailed,
} = buildFilteredSelections(messagesRef)

  const seletedRetailer = computed(
    () => store.getters?.getSelectedRetailer || null,
  )

  const getSubHeader = buildSubHeader({
 isAllSuccess, isAllFailed, successfullySharedSelections, failedSharedSelections, seletedRetailer,
})

  function onClickFollowUp(closeModalFn) {
    const trackObject = {
      retailer: seletedRetailer.value,
      selections: failedSharedSelections.value,
    }
    analytics.track(SHARE_MULTIPLE_SELECTIONS.BRAND_CLICKS_FOLLOW_UP_BUTTON, trackObject)

    isProcessingFollowUp.value = true
    const payload = {
      retailerId: seletedRetailer.value?.retailerId,
      selectionIds: failedSharedSelections.value.map(selection => selection._id),
    }

    sendFollowUpRequest(payload)
      .then(res => handleFollowUpSuccess(res, closeModalFn, trackObject))
      .catch(err => handleFollowUpError(err, trackObject))
      .finally(() => {
        isProcessingFollowUp.value = false
      })
  }

  function onCloseModal(closeModalFn) {
    closeModalFn()
    analytics.track(SHARE_MULTIPLE_SELECTIONS.BRAND_CLICKS_DONT_FOLLOW_UP_BUTTON)
    analytics.track(SHARE_MULTIPLE_SELECTIONS.BRAND_CLOSES_FOLLOW_UP_POPUP)
  }

  return {
    isProcessingFollowUp,
    successfullySharedSelections,
    failedSharedSelections,
    isAllSuccess,
    isAllFailed,
    seletedRetailer,
    getSubHeader,
    onClickFollowUp,
    onCloseModal,
  }
}
